// src/components/ViewActions.tsx
import React, { useEffect, useState } from "react";
import { sortByTimestampDescending } from "../shared/helper";
import { getSpaceshipActionCost, getAgencyActionCost, SpaceshipAction, AgencyAction, ActionStatus, getActionEmoji } from "../shared/constants";
import { API_BASE_URL } from "../utils/api";

interface Action {
  action_id: string;
  player_id: string;
  turn: number;
  action: string;
  status: string;
  details: string | null;
  collaborators?: string | null;
  timestamp: number | null;
  scanning_agency_scan_lvl?: number | null;
}

interface ViewActionsProps {
  playerId: string;
  playerRole?: string;
}

export default function ViewActions({ playerId, playerRole }: ViewActionsProps) {
  const [actions, setActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  const fetchActions = React.useCallback(() => {
    if (!playerId) return;

    console.log("Fetching actions for player:", playerId);
    fetch(`${API_BASE_URL}/players/actions?player_id=${encodeURIComponent(playerId)}`)
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch actions");
        return res.json();
      })
      .then((data: Action[]) => {
        const sorted = sortByTimestampDescending(data, (action) => action.timestamp);
        setActions(sorted);
        setLoading(false);
        console.log("Actions fetched:", data.length);
      })
      .catch(err => {
        console.error(err);
        setError(err.message);
        setLoading(false);
      });
  }, [playerId]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  // Listen for action updates and append to local state instead of refetching
  useEffect(() => {
    console.log("useEffect for action updates");
    const handleActionUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      const actionData = customEvent.detail;
      
      if (actionData && actionData.action_id) {
        // Action data is included in the event, add it to local state
        console.log("New action received via event:", actionData);
        
        // Only process if this action belongs to this player
        if (actionData.player_id !== playerId) {
          return;
        }
        
        setActions(prevActions => {
          // Check if action already exists (avoid duplicates)
          const exists = prevActions.some(a => a.action_id === actionData.action_id);
          if (exists) {
            // Update existing action if status changed
            return prevActions.map(a => 
              a.action_id === actionData.action_id ? {
                ...a,
                ...actionData,
                status: actionData.status || a.status,
                details: actionData.details !== undefined ? actionData.details : a.details,
                collaborators: actionData.collaborators !== undefined ? actionData.collaborators : a.collaborators,
                timestamp: actionData.timestamp !== undefined ? actionData.timestamp : a.timestamp,
                scanning_agency_scan_lvl: actionData.scanning_agency_scan_lvl !== undefined ? actionData.scanning_agency_scan_lvl : a.scanning_agency_scan_lvl
              } : a
            );
          }
          // Add new action and sort by timestamp
          const newActions = [...prevActions, {
            action_id: actionData.action_id,
            player_id: actionData.player_id || playerId,
            turn: actionData.turn,
            action: actionData.action,
            status: actionData.status,
            details: actionData.details || null,
            collaborators: actionData.collaborators || null,
            timestamp: actionData.timestamp || Date.now(),
            scanning_agency_scan_lvl: actionData.scanning_agency_scan_lvl !== undefined ? actionData.scanning_agency_scan_lvl : null
          }];
          return sortByTimestampDescending(newActions, (a) => a.timestamp || 0);
        });
      } else {
        // Fallback: If action data is incomplete (legacy event without data), refetch
        // This should rarely happen since WebSocket events include data
        console.warn("Action event without data, refetching actions as fallback");
        fetchActions();
      }
    };

    // Listen for custom events when actions are created
    window.addEventListener('actionCreated', handleActionUpdate);
    console.log("Event listener registered for 'actionCreated'");

    return () => {
      window.removeEventListener('actionCreated', handleActionUpdate);
      console.log("Event listener removed for 'actionCreated'");
    };
  }, [playerId, fetchActions]);

  return (
    <div style={{ 
      border: "1px solid #dee2e6", 
      padding: "1.5rem", 
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
    }}>
      <h2 style={{ margin: "0 0 1rem 0", color: "#212529", fontSize: "1.25rem" }}>Actions</h2>
      
      {loading && (
        <div style={{ padding: "2rem", textAlign: "center", color: "#6c757d" }}>
          Loading actions...
        </div>
      )}
      
      {error && (
        <div style={{ 
          padding: "1rem", 
          backgroundColor: "#f8d7da", 
          color: "#721c24", 
          borderRadius: "4px",
          border: "1px solid #f5c6cb"
        }}>
          {error}
        </div>
      )}
      
      {!loading && !error && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ 
            width: "100%", 
            borderCollapse: "collapse",
            fontSize: "0.875rem"
          }}>
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Turn</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Action</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Cost</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Status</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Collaborators</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {actions.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: "2rem", textAlign: "center", color: "#6c757d", fontStyle: "italic" }}>
                    No actions yet.
                  </td>
                </tr>
              ) : (
                actions.map(action => {
                // Calculate cost based on action type
                let cost = 0;
                try {
                  if (Object.values(SpaceshipAction).includes(action.action as SpaceshipAction)) {
                    cost = getSpaceshipActionCost(action.action as SpaceshipAction);
                  } else if (Object.values(AgencyAction).includes(action.action as AgencyAction)) {
                    cost = getAgencyActionCost(action.action as AgencyAction);
                  }
                } catch (e) {
                  // If action doesn't match, cost remains 0
                }
                
                const getStatusColors = () => {
                  switch (action.status) {
                    case ActionStatus.COMPLETED:
                      return { backgroundColor: "#d4edda", color: "#155724" };
                    case ActionStatus.CANCELED:
                      return { backgroundColor: "#f8d7da", color: "#721c24" };
                    case ActionStatus.ADMIN:
                      return { backgroundColor: "#e3f2fd", color: "#1976d2" };  // Same blue-ish color as admin messages
                    case ActionStatus.PENDING:
                      return { backgroundColor: "#fff3cd", color: "#856404" };  // Yellowish for pending
                    default:
                      return { backgroundColor: "#fff3cd", color: "#856404" };  // Default to yellowish
                  }
                };
                const colors = getStatusColors();
                const hasCollaborators = Boolean(action.collaborators && action.collaborators.trim() !== "");
                
                // Determine row background color based on status
                let rowBackgroundColor = "transparent";
                switch (action.status) {
                  case ActionStatus.ADMIN:
                    rowBackgroundColor = "#e3f2fd";  // Blue-ish background (same as admin messages)
                    break;
                  case ActionStatus.PENDING:
                    rowBackgroundColor = "#fff3cd";  // Yellowish background
                    break;
                  case ActionStatus.CANCELED:
                    rowBackgroundColor = "#f8d7da";  // Light red background
                    break;
                  case ActionStatus.COMPLETED:
                    rowBackgroundColor = "#d4edda";  // Light green background
                    break;
                  default:
                    rowBackgroundColor = hasCollaborators ? "#fff7e6" : "transparent";
                }

                return (
                  <tr
                    key={action.action_id}
                    style={{
                      borderBottom: "1px solid #dee2e6",
                      backgroundColor: rowBackgroundColor
                    }}
                  >
                    <td style={{ padding: "0.75rem", color: "#212529" }}>{action.turn}</td>
                    <td style={{ padding: "0.75rem", color: "#212529" }}>
                      {(() => {
                        try {
                          const actionEnum = Object.values(SpaceshipAction).includes(action.action as SpaceshipAction)
                            ? (action.action as SpaceshipAction)
                            : (action.action as AgencyAction);
                          const emoji = getActionEmoji(actionEnum);
                          return `${emoji} ${action.action}`;
                        } catch {
                          return action.action;
                        }
                      })()}
                    </td>
                    <td style={{ padding: "0.75rem", color: "#212529" }}>{cost}</td>
                    <td style={{ padding: "0.75rem", color: "#212529" }}>
                      <span style={{
                        padding: "0.25rem 0.5rem",
                        backgroundColor: colors.backgroundColor,
                        color: colors.color,
                        borderRadius: "4px",
                        fontSize: "0.85rem",
                        fontWeight: "bold"
                      }}>
                        {action.status}
                      </span>
                    </td>
                    <td style={{ padding: "0.75rem", color: "#212529", maxWidth: "200px", wordWrap: "break-word" }}>
                      {action.collaborators || "—"}
                    </td>
                    <td style={{ padding: "0.75rem", color: "#212529", maxWidth: "300px", wordWrap: "break-word" }}>
                      <span style={{ whiteSpace: "pre-line" }}>{action.details || "-"}</span>
                    </td>
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

